import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { useI18n } from '../../../hooks/useI18n';
import { TranslationKeys } from '../../../i18n/locales/defaultResources';
import {
  AppTitle,
  BPListContainer,
  CloseIconContainer,
  HamburgerIcon,
  HeaderContainer,
  LogoText,
  ProfileContainer,
  StyledLogo,
  Subtitle,
} from './styles';
import Logo from '../../../assets/Logo';
import BurgerMenuIcon from '../../../assets/icons/sidebarIcons/BurgerMenuIcon';
import Avatar from '../../components/Avatar/Avatar';
import { useContextStore } from '../../../stores/storeRoot';
import BusinessPartnerSelector from '../../features/bp/BusinessPartnerSelector/BusinessPartnerSelector';
import { BusinessPartnerSelectorItem } from '../../features/bp/BusinessPartnerSelector/types';
import OnSwitchingBPWarningModal from '../../features/common_modals/OnSwitchingBPWarningModal/OnSwitchingBPWarningModal';

const MobileHeader: React.FC<{
  onMenuClick: () => void;
  isMenuOpen: boolean;
}> = ({ onMenuClick, isMenuOpen }) => {
  const { t } = useI18n();
  const {
    activeBpNumber,
    businessPartners,
    contextUpdating,
    switchBusinessPartner,
    accessibleBpRanges,
    nordzuckerInternalUser,
    finishAccessOnBehalf,
    isAccessOnBehalf,
  } = useContextStore();

  const [isBPListVisible, setIsBPListVisible] = useState(false);
  const [bpSelectorItems, setBpSelectorItems] = useState<
    BusinessPartnerSelectorItem[]
  >([]);
  const [aobInputValue, setAobInputValue] = useState<string | undefined>();
  const [aobActivationError, setAobActivationError] =
    useState<TranslationKeys>();
  const [activeBP, setActiveBP] = useState<string | undefined>(activeBpNumber);
  const [isOnSwitchingBPWarningModalOpen, setIsOnSwitchingBPWarningModalOpen] =
    useState<boolean>(false);
  const [currentInactiveBp, setCurrentInactiveBp] = useState<
    BusinessPartnerSelectorItem | undefined
  >();

  useEffect(() => {
    setBpSelectorItems(
      businessPartners
        .map((bpa) => {
          return {
            businessPartnerDisplayName: bpa.displayName ?? '',
            businessPartnerNumber: bpa.businessPartnerNumber ?? '',
            inactive: !bpa.activated || !bpa.initialized,
          };
        })
        .filter((bp) => bp.businessPartnerNumber)
    );
    setActiveBP(activeBpNumber);
  }, [activeBP, businessPartners]);

  const toggleBPList = () => {
    setIsBPListVisible(!isBPListVisible);
  };

  const handleBPSelect = (bp: BusinessPartnerSelectorItem) => {
    if (bp.inactive) {
      setIsOnSwitchingBPWarningModalOpen(true);
      setCurrentInactiveBp(bp);
      document.body.style.overflow = 'hidden';
      setIsBPListVisible(false);
    } else {
      setIsOnSwitchingBPWarningModalOpen(false);
      document.body.style.overflow = 'overlay';
      switchBusinessPartner(bp.businessPartnerNumber, false).then(() =>
        setIsBPListVisible(false)
      );
    }
  };

  const aobInputValueChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNums = event.target.value.replace(/[^0-9]/g, '');
    setAobInputValue(onlyNums);
  };

  const handleAobStart = async (bp: string) => {
    let canSwitch = false;
    if (accessibleBpRanges) {
      const parsedValue = Number.parseInt(bp);
      canSwitch = accessibleBpRanges.some((range) => {
        return (
          parsedValue >= range.low &&
          (range.high !== undefined
            ? parsedValue <= range.high!
            : parsedValue == range.low)
        );
      });
    }
    if (canSwitch) {
      try {
        await switchBusinessPartner(bp, true);
        setIsBPListVisible(false);
      } catch (err) {
        if (err && typeof err === 'object' && 'statusCode' in err) {
          if (err.statusCode === 'NotFound') {
            setAobActivationError(
              TranslationKeys.accessonbehalfactivationbox_error_bpnotfound
            );
          } else if (err.statusCode === 'Unauthorized') {
            setAobActivationError(
              TranslationKeys.accessonbehalfactivationbox_error_bpnotaccessible
            );
          } else {
            setAobActivationError(TranslationKeys.common_genericerror);
          }
        } else {
          setAobActivationError(TranslationKeys.common_genericerror);
        }
      }
    } else {
      setAobActivationError(
        TranslationKeys.accessonbehalfactivationbox_error_invalidbpnumber
      );
    }
  };

  const handleAoBEnd = () => {
    finishAccessOnBehalf().then(() => {});
  };

  return (
    <>
      <OnSwitchingBPWarningModal
        isOpen={isOnSwitchingBPWarningModalOpen}
        bp={currentInactiveBp}
        onClose={() => {
          setIsOnSwitchingBPWarningModalOpen(false);
          document.body.style.overflow = 'overlay';
        }}
      />{' '}
      <HeaderContainer id="mobile-header-container">
        <HamburgerIcon onClick={onMenuClick}>
          {isMenuOpen ? <CloseIcon /> : <BurgerMenuIcon />}
        </HamburgerIcon>
        <StyledLogo>
          <Logo />
          <LogoText>
            <AppTitle>{t(TranslationKeys.mobileHeader_appTitle)}</AppTitle>
            <Subtitle>{t(TranslationKeys.mobileHeader_subtitle)}</Subtitle>
          </LogoText>
        </StyledLogo>
        <ProfileContainer
          onClick={
            businessPartners && businessPartners.length > 0
              ? toggleBPList
              : undefined
          }
          className={isBPListVisible ? 'center-close-icon' : 'center-avatar'}
        >
          {businessPartners &&
            businessPartners.length > 0 &&
            (isBPListVisible ? (
              <CloseIconContainer>
                <CloseIcon />
              </CloseIconContainer>
            ) : (
              <>
                <Avatar />
                <span>{activeBpNumber ? activeBpNumber : 'Select BP'}</span>
              </>
            ))}
        </ProfileContainer>
        {isBPListVisible && (
          <BPListContainer>
            <BusinessPartnerSelector
              activeBPNumber={activeBpNumber}
              bpNumbers={bpSelectorItems}
              onBPSelect={handleBPSelect}
              isLoading={contextUpdating}
              hasAob={nordzuckerInternalUser}
              aobActivationError={aobActivationError}
              aobInputValue={aobInputValue}
              aobInputValueChanged={aobInputValueChanged}
              onAobStart={handleAobStart}
              isListOpen={isBPListVisible}
              setIsListOpen={setIsBPListVisible}
              onAobEnd={handleAoBEnd}
              isAccessOnBehalf={isAccessOnBehalf}
            />
          </BPListContainer>
        )}
      </HeaderContainer>
    </>
  );
};

export default MobileHeader;
